/**
 * SOLA Tool Type Definitions
 *
 * Comprehensive type system for AI SDK v6 tool rendering.
 * Source: PRD-TOOL-COMPONENTS-REFACTOR.md and AI SDK v6.0.0-beta.156
 */

import type { ComponentType } from 'react';

// ============== Tool Status Types ==============

/**
 * Tool execution status with granular states
 */
export type ToolStatus =
  | 'idle'              // Not started
  | 'input-streaming'   // Receiving input params
  | 'input-ready'       // Input complete, about to execute
  | 'executing'         // Tool running
  | 'streaming'         // Receiving preliminary results
  | 'complete'          // Final result received
  | 'error';            // Execution failed

/**
 * UI Tool Invocation state from AI SDK v6
 * Maps to the UIToolInvocation states in the SDK
 */
export type UIToolInvocationState =
  | 'input-streaming'     // Tool input being streamed
  | 'input-available'     // Tool input complete, executing
  | 'approval-requested'  // Human-in-the-loop: waiting for approval
  | 'approval-responded'  // Human responded to approval
  | 'output-available'    // Tool output ready (may be preliminary)
  | 'output-error'        // Tool execution failed
  | 'output-denied';      // Human denied tool execution

// ============== Base Tool Part Types ==============

/**
 * Base tool part interface from AI SDK
 */
export interface BaseToolPart {
  type: `tool-${string}`;
  toolCallId: string;
  toolName: string;
  state: 'input-streaming' | 'input-available' | 'output-available' | 'output-error';
  input?: unknown;
  output?: unknown;
  errorText?: string;
  preliminary?: boolean;  // From generator yield
  title?: string;         // Human-readable title
}

/**
 * Generic tool part with typed input/output
 */
export interface TypedToolPart<
  TName extends string,
  TInput = unknown,
  TOutput = unknown
> extends BaseToolPart {
  type: `tool-${TName}`;
  toolName: TName;
  input?: TInput;
  output?: TOutput;
}

// ============== Aggregated Tool Call ==============

/**
 * Tool output entry with metadata
 */
export interface ToolOutputEntry {
  output: unknown;
  preliminary: boolean;
  timestamp: number;
}

/**
 * Approval state for human-in-the-loop tools
 */
export interface ToolApprovalState {
  id: string;
  approved?: boolean;
  reason?: string;
}

/**
 * Aggregated tool call - groups all stream chunks by toolCallId
 * This is the primary interface for tool rendering
 */
export interface AggregatedToolCall<TInput = unknown, TOutput = unknown> {
  toolCallId: string;
  toolName: string;
  title?: string;

  // Input progression
  inputChunks: string[];           // From input-delta chunks
  input: TInput | undefined;       // Parsed input when available
  inputComplete: boolean;

  // Output progression
  outputs: ToolOutputEntry[];
  finalOutput: TOutput | undefined;

  // Current state
  state: UIToolInvocationState;
  errorText?: string;

  // Approval state (human-in-the-loop)
  approval?: ToolApprovalState;
}

// ============== Tool Display Props ==============

/**
 * Standard props for all tool display components
 */
export interface ToolDisplayProps<TInput = unknown, TOutput = unknown> {
  toolCall: AggregatedToolCall<TInput, TOutput>;
  isStreaming: boolean;
}

/**
 * Props for legacy tool part-based displays (migration period)
 */
export interface LegacyToolDisplayProps<TInput = unknown, TOutput = unknown> {
  toolPart: TypedToolPart<string, TInput, TOutput>;
  isStreaming: boolean;
}

// ============== Tool State Hook Types ==============

/**
 * Tool state derived from aggregated tool call
 * Provides convenience properties for rendering
 */
export interface ToolState<TOutput = unknown> {
  status: ToolStatus;
  progress: number;                  // 0-100
  input: unknown;
  output: TOutput | null;
  partialOutput: Partial<TOutput> | null;
  error: string | null;
  isPreliminary: boolean;
  isLoading: boolean;
  isComplete: boolean;
  isError: boolean;
}

/**
 * Derive ToolState from AggregatedToolCall
 */
export function deriveToolState<TOutput = unknown>(
  toolCall: AggregatedToolCall<unknown, TOutput>
): ToolState<TOutput> {
  const status = mapInvocationStateToStatus(toolCall.state, toolCall.outputs.length);
  const lastOutput = toolCall.outputs[toolCall.outputs.length - 1];
  const isPreliminary = lastOutput?.preliminary ?? false;

  return {
    status,
    progress: calculateProgress(toolCall),
    input: toolCall.input,
    output: (toolCall.finalOutput as TOutput) ?? null,
    partialOutput: isPreliminary ? (lastOutput?.output as Partial<TOutput>) : null,
    error: toolCall.errorText ?? null,
    isPreliminary,
    isLoading: ['input-streaming', 'input-available'].includes(toolCall.state),
    isComplete: toolCall.state === 'output-available' && !isPreliminary,
    isError: toolCall.state === 'output-error',
  };
}

/**
 * Map UIToolInvocationState to ToolStatus
 */
function mapInvocationStateToStatus(state: UIToolInvocationState, outputCount: number): ToolStatus {
  switch (state) {
    case 'input-streaming':
      return 'input-streaming';
    case 'input-available':
      return 'executing';
    case 'approval-requested':
    case 'approval-responded':
      return 'executing';
    case 'output-available':
      return outputCount > 1 ? 'streaming' : 'complete';
    case 'output-error':
    case 'output-denied':
      return 'error';
    default:
      return 'idle';
  }
}

/**
 * Calculate progress percentage from tool state
 */
function calculateProgress(toolCall: AggregatedToolCall): number {
  switch (toolCall.state) {
    case 'input-streaming':
      return 10;
    case 'input-available':
      return 30;
    case 'approval-requested':
      return 40;
    case 'approval-responded':
      return 50;
    case 'output-available':
      // If we have preliminary outputs, estimate progress
      const preliminaryCount = toolCall.outputs.filter(o => o.preliminary).length;
      if (preliminaryCount > 0) {
        return 50 + Math.min(45, preliminaryCount * 10);
      }
      return toolCall.finalOutput ? 100 : 60;
    case 'output-error':
    case 'output-denied':
      return 100;
    default:
      return 0;
  }
}

// ============== Tool Registry Types ==============

/**
 * Tool display component type
 */
export type ToolDisplayComponent<TInput = unknown, TOutput = unknown> =
  ComponentType<ToolDisplayProps<TInput, TOutput>>;

/**
 * Tool display registry mapping tool names to components
 */
export type ToolDisplayRegistry = Record<string, ToolDisplayComponent>;

// ============== Tool Card Primitive Props ==============

/**
 * Props for ToolCard container component
 */
export interface ToolCardProps {
  status: ToolStatus;
  children: React.ReactNode;
  className?: string;
}

/**
 * Props for ToolHeader component
 */
export interface ToolHeaderProps {
  title: string;
  toolName: string;
  status: ToolStatus;
  progress?: number;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  icon?: React.ReactNode;
}

/**
 * Props for ToolBody component
 */
export interface ToolBodyProps {
  isOpen: boolean;
  children: React.ReactNode;
}

/**
 * Props for ToolProgress component
 */
export interface ToolProgressProps {
  value: number;
  variant?: 'linear' | 'circular' | 'dots';
  label?: string;
}

/**
 * Props for ToolError component
 */
export interface ToolErrorProps {
  error: string;
  onRetry?: () => void;
}

/**
 * Props for ToolSkeleton component
 */
export interface ToolSkeletonProps {
  variant?: 'card' | 'list' | 'table';
  rows?: number;
}

// ============== Entity Display Types ==============

/**
 * Generic entity for EntityCard display
 */
export interface DisplayEntity {
  id: string | number;
  name?: string;
  title?: string;
  email?: string;
  avatar?: string;
  status?: string;
  tags?: string[];
  [key: string]: unknown;
}

/**
 * Props for EntityCard component
 */
export interface EntityCardProps {
  entity: DisplayEntity;
  type: 'candidate' | 'contact' | 'float';
  onClick?: () => void;
  compact?: boolean;
}

/**
 * Props for DataTable component
 */
export interface DataTableProps<T = Record<string, unknown>> {
  data: T[];
  columns: Array<{
    key: keyof T | string;
    header: string;
    width?: string | number;
    render?: (value: unknown, row: T) => React.ReactNode;
  }>;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  emptyMessage?: string;
}

// ============== Tool Name Constants ==============

/**
 * All known tool names for type-safe registry lookups
 */
export const TOOL_NAMES = {
  // Analytics
  queryData: 'queryData',
  exportAnalyticsData: 'exportAnalyticsData',
  planReport: 'planReport',
  renderReportChart: 'renderReportChart',

  // Candidates - Core
  searchCandidates: 'searchCandidates',
  getCandidate: 'getCandidate',
  createCandidate: 'createCandidate',
  deleteCandidate: 'deleteCandidate',
  updateCandidateData: 'updateCandidateData',
  addCandidateNote: 'addCandidateNote',
  importCandidateFromResume: 'importCandidateFromResume',
  generateResume: 'generateResume',

  // Candidates - Skills
  addCandidateSkill: 'addCandidateSkill',
  removeCandidateSkill: 'removeCandidateSkill',

  // Candidates - Roles
  addCandidateRole: 'addCandidateRole',
  updateCandidateRole: 'updateCandidateRole',
  removeCandidateRole: 'removeCandidateRole',

  // Candidates - Projects
  addCandidateProject: 'addCandidateProject',
  updateCandidateProject: 'updateCandidateProject',
  removeCandidateProject: 'removeCandidateProject',

  // Candidates - Education
  addCandidateEducation: 'addCandidateEducation',
  updateCandidateEducation: 'updateCandidateEducation',
  removeCandidateEducation: 'removeCandidateEducation',

  // Contacts
  searchContacts: 'searchContacts',
  getContact: 'getContact',
  createContact: 'createContact',
  updateContact: 'updateContact',
  deleteContact: 'deleteContact',

  // Floats - Core
  searchFloats: 'searchFloats',
  getFloat: 'getFloat',
  createFloat: 'createFloat',
  updateFloat: 'updateFloat',
  deleteFloat: 'deleteFloat',

  // Floats - Utilities
  fetchCandidatesForFloat: 'fetchCandidatesForFloat',
  duplicateFloat: 'duplicateFloat',

  // Floats - Engagement
  getFloatInteractions: 'getFloatInteractions',
  createShareLink: 'createShareLink',

  // Research
  searchWeb: 'searchWeb',

  // Email Campaigns
  getEmailDraft: 'getEmailDraft',
  createFloatEmail: 'createFloatEmail',
  sendTestEmail: 'sendTestEmail',
  sendEmailCampaign: 'sendEmailCampaign',
  createScheduledMessage: 'createScheduledMessage',
  getScheduledMessages: 'getScheduledMessages',

  // Dashboard & Stats
  getDashboardStats: 'getDashboardStats',
  getTeamDashboardStats: 'getTeamDashboardStats',
  getTopLeads: 'getTopLeads',

  // Integrations
  getIntegrationStatus: 'getIntegrationStatus',
  disconnectIntegration: 'disconnectIntegration',
  proxyIntegrationRequest: 'proxyIntegrationRequest',
  clearIntegrationCache: 'clearIntegrationCache',

  // Team
  getTeamCandidates: 'getTeamCandidates',
  getTeamCandidate: 'getTeamCandidate',
  duplicateTeamCandidate: 'duplicateTeamCandidate',
  getTeamFloats: 'getTeamFloats',
  getTeamFloat: 'getTeamFloat',
  duplicateTeamFloat: 'duplicateTeamFloat',
  getTeamContacts: 'getTeamContacts',
  getTeamContact: 'getTeamContact',
  duplicateTeamContact: 'duplicateTeamContact',

  // Notifications
  getNotifications: 'getNotifications',
  markNotificationsRead: 'markNotificationsRead',

  // Tags
  getTags: 'getTags',

  // Suggestions
  suggestFollowUps: 'suggestFollowUps',
} as const;

export type ToolName = typeof TOOL_NAMES[keyof typeof TOOL_NAMES];

// ============== Human-in-the-Loop Tools ==============

/**
 * Tools that require human approval before execution
 */
export const APPROVAL_REQUIRED_TOOLS: ToolName[] = [
  'sendEmailCampaign',
  'deleteCandidate',
  'deleteContact',
  'deleteFloat',
  'disconnectIntegration',
];

/**
 * Check if a tool requires human approval
 */
export function requiresApproval(toolName: string): boolean {
  return APPROVAL_REQUIRED_TOOLS.includes(toolName as ToolName);
}

// ============== Judge Tool Types ==============

export interface JudgeMetric {
  score: number;
  reason: string;
  suggestion: string;
}

export interface JudgeResult {
  overallScore: number;
  conversationState: 'ready_to_respond' | 'on_track' | 'needs_improvement' | 'stuck';
  metrics: {
    intentClarity: JudgeMetric;
    dataAvailability: JudgeMetric;
    toolRelevance: JudgeMetric;
    actionCompleteness: JudgeMetric;
    informationAccuracy: JudgeMetric;
    responseUtility: JudgeMetric;
    contextRetention: JudgeMetric;
    processEfficiency: JudgeMetric;
  };
  priorityActions: string[];
  missingContextSummary: string | null;
  progressTowardsGoal: number;
  confidence: number;
  blockers: string[];
  recommendedNextSteps: string[];
  shouldContinue: boolean;
  availableTools: string[];
  evaluationStage: string;
}
